import type { ReactNode } from "react";
import { headingFontStyle } from "../Theme/headingFontStyle";
import {
  alignClasses,
  colorClasses,
  sizeClasses,
  weightClasses,
  type TextAlign,
  type TextColor,
  type TextSize,
  type TextWeight,
} from "./textClasses";

type HeadingLevel = 1 | 2 | 3 | 4;

interface HeadingProps {
  /**
   * The heading rank — renders the matching semantic element (`h1`–`h4`) and
   * picks a default size/weight. Choose by document outline, not by how big you
   * want the text: override `size` for the visual scale.
   */
  level: HeadingLevel;
  /** Override the per-level default size. */
  size?: TextSize;
  /** Override the per-level default weight. */
  weight?: TextWeight;
  color?: TextColor;
  align?: TextAlign;
  children: ReactNode;
}

const defaultSize: Record<HeadingLevel, TextSize> = {
  1: "3xl",
  2: "2xl",
  3: "xl",
  4: "base",
};

const defaultWeight: Record<HeadingLevel, TextWeight> = {
  1: "bold",
  2: "bold",
  3: "semibold",
  4: "bold",
};

/**
 * A page or section title on the brand headline face (Neue Haas Unica, via
 * `--font-heading`). Always use this for titles rather than styling a `T` — it
 * owns the semantic tag and the heading font so the two can't drift apart.
 */
export function Heading({
  level,
  size,
  weight,
  color = "default",
  align,
  children,
}: HeadingProps) {
  const Tag = `h${level}` as const;
  const classes = [
    sizeClasses[size ?? defaultSize[level]],
    weightClasses[weight ?? defaultWeight[level]],
    colorClasses[color],
    align ? alignClasses[align] : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Tag className={classes} style={headingFontStyle()}>
      {children}
    </Tag>
  );
}
